import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

const ChallengeQuestion = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-question',
      'relative flex flex-grow flex-col gap-5 rounded-2xl border border-solid border-neutral-lightest bg-gradient-to-tr from-white from-[20.02%] to-[#F5F5F5] to-[85.13%] p-[max(12px,3.5vw)] shadow-dropshadow-lg',
      className
    )}
    {...props}
  />
));
ChallengeQuestion.displayName = 'ChallengeQuestion';

const ChallengeQuestionCount = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-1 self-end text-xs font-light text-primary lg:absolute lg:right-4 lg:top-4', '[&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0', className)}
    {...props}
  />
));
ChallengeQuestionCount.displayName = 'ChallengeQuestionCount';

const ChallengeQuestionHeader = React.forwardRef(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn('flex min-h-[136px] items-center justify-center gap-3 rounded-sm bg-white p-2 text-center', className)}
    {...props}
  />
));
ChallengeQuestionHeader.displayName = 'ChallengeQuestionHeader';

const ChallengeQuestionTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('mb-0 text-base font-bold leading-tight text-primary-dark', className)}
    {...props}
  />
));
ChallengeQuestionTitle.displayName = 'ChallengeQuestionTitle';

const ChallengeQuestionBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('grow', className)}
    {...props}
  />
));
ChallengeQuestionBody.displayName = 'ChallengeQuestionBody';

const ChallengeQuestionList = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-2', className)}
    {...props}
  />
));
ChallengeQuestionList.displayName = 'ChallengeQuestionList';

const ChallengeQuestionRadio = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-challenge-question-radio', 'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border border-solid border-neutral', className)}
    {...props}
  />
));
ChallengeQuestionRadio.displayName = 'ChallengeQuestionRadio';

const ChallengeQuestionIcon = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    className={cn('absolute -right-2 -top-2 w-8', className)}
    {...props}
  />
));
ChallengeQuestionIcon.displayName = 'ChallengeQuestionIcon';

const challengeQuestionAlternativeVariants = cva('relative flex cursor-pointer appearance-none items-center gap-2 rounded-sm border border-neutral-lightest bg-white p-2 text-left text-neutral-dark', {
  variants: {
    variant: {
      success: [
        'bg-success text-white',
        '[&_.c-challenge-question-radio]:border-2 [&_.c-challenge-question-radio]:border-white',
        '[&_.c-challenge-question-radio]:after:block [&_.c-challenge-question-radio]:after:h-2 [&_.c-challenge-question-radio]:after:w-2 [&_.c-challenge-question-radio]:after:rounded-full [&_.c-challenge-question-radio]:after:bg-white [&_.c-challenge-question-radio]:after:content-[""]'
      ],
      danger: [
        'bg-danger text-white',
        '[&_.c-challenge-question-radio]:border-2 [&_.c-challenge-question-radio]:border-white',
        '[&_.c-challenge-question-radio]:after:block [&_.c-challenge-question-radio]:after:h-2 [&_.c-challenge-question-radio]:after:w-2 [&_.c-challenge-question-radio]:after:rounded-full [&_.c-challenge-question-radio]:after:bg-white [&_.c-challenge-question-radio]:after:content-[""]'
      ]
    }
  }
});

const ChallengeQuestionAlternative = React.forwardRef(({ className, variant, ...props }, ref) => (
  <button
    ref={ref}
    className={cn('c-challenge-question-alternative', challengeQuestionAlternativeVariants({ variant }), className)}
    {...props}
  />
));
ChallengeQuestionAlternative.displayName = 'ChallengeQuestionAlternative';

const ChallengeQuestionFooter = React.forwardRef(({ className, ...props }, ref) => (
  <footer
    ref={ref}
    className={cn('text-center', className)}
    {...props}
  />
));
ChallengeQuestionFooter.displayName = 'ChallengeQuestionFooter';

const ChallengeQuestionComment = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('mt-5 flex flex-wrap items-start gap-2 gap-x-4 text-left', className)}
    {...props}
  />
));
ChallengeQuestionComment.displayName = 'ChallengeQuestionComment';

const ChallengeQuestionCommentContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex w-80 max-w-full flex-grow flex-col gap-2 rounded-lg bg-bright p-2 text-base font-light text-neutral-dark [&>*]:mb-0', className)}
    {...props}
  />
));
ChallengeQuestionCommentContent.displayName = 'ChallengeQuestionCommentContent';

export {
  ChallengeQuestion,
  ChallengeQuestionCount,
  ChallengeQuestionHeader,
  ChallengeQuestionTitle,
  ChallengeQuestionBody,
  ChallengeQuestionList,
  ChallengeQuestionRadio,
  ChallengeQuestionIcon,
  ChallengeQuestionAlternative,
  ChallengeQuestionFooter,
  ChallengeQuestionComment,
  ChallengeQuestionCommentContent
};
